import {useIntl} from '@sail/intl';
import {useMemo} from 'react';
import {useMemoizedValue} from '@sail/react';
import type {EmailValidationOptions} from './types';
import {DISPOSABLE_DOMAINS} from './disposableEmailDomains';
import {
  invalidEmailMsg,
  disposableEmailMsg,
  emailDomainNotAllowedMsg,
  emailDomainBlockedMsg,
  emailTLDNotAllowedMsg,
  emailTooLongMsg,
} from './messages';

/**
 * Email validation regex that supports RFC 5322 standard
 * Supports local part with dots, plus addressing, and international characters
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * More restrictive ASCII-only email regex
 */
const ASCII_EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Hook that returns an email validation function for use with form fields
 *
 * @example
 * ```tsx
 * const validateEmail = useEmailValidation({
 *   allowDisposable: false,
 *   allowedDomains: ['stripe.com', 'example.com'],
 * });
 *
 * <Form form={form}>
 *   {...form.field('email', {
 *     required: true,
 *     validate: validateEmail,
 *   })}
 * </Form>
 * ```
 */
export function useEmailValidation(options: EmailValidationOptions = {}) {
  const {formatMessage} = useIntl();

  const {
    allowDisposable = false,
    allowedDomains,
    blockedDomains,
    allowPlusAddressing = true,
    requiredTLDs,
    maxLength = 320,
    allowInternational = true,
    message,
  } = options;

  const stableAllowedDomains = useMemoizedValue(allowedDomains);
  const stableBlockedDomains = useMemoizedValue(blockedDomains);
  const stableRequiredTLDs = useMemoizedValue(requiredTLDs);

  return useMemo(() => {
    return (value: string): string | boolean => {
      if (!value) {
        return true;
      }

      // Check length
      if (value.length > maxLength) {
        return (
          message ||
          formatMessage(emailTooLongMsg, {
            maxLength,
          })
        );
      }

      // Validate basic email format
      const emailRegex = allowInternational ? EMAIL_REGEX : ASCII_EMAIL_REGEX;
      if (!emailRegex.test(value)) {
        return message || formatMessage(invalidEmailMsg);
      }

      // Extract domain
      const [localPart, domain] = value.split('@');
      // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
      const lowerDomain = domain.toLowerCase();

      // Check plus addressing
      // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
      if (!allowPlusAddressing && localPart.includes('+')) {
        return message || formatMessage(invalidEmailMsg);
      }

      // Check disposable domains
      if (!allowDisposable && DISPOSABLE_DOMAINS.has(lowerDomain)) {
        return message || formatMessage(disposableEmailMsg);
      }

      // Check allowed domains
      if (stableAllowedDomains && stableAllowedDomains.length > 0) {
        const isAllowed = stableAllowedDomains.some(
          (allowed) => lowerDomain === allowed.toLowerCase(),
        );
        if (!isAllowed) {
          return (
            message ||
            formatMessage(emailDomainNotAllowedMsg, {domain: lowerDomain})
          );
        }
      }

      // Check blocked domains
      if (stableBlockedDomains && stableBlockedDomains.length > 0) {
        const isBlocked = stableBlockedDomains.some(
          (blocked) => lowerDomain === blocked.toLowerCase(),
        );
        if (isBlocked) {
          return (
            message ||
            formatMessage(emailDomainBlockedMsg, {domain: lowerDomain})
          );
        }
      }

      // Check required TLDs
      if (stableRequiredTLDs && stableRequiredTLDs.length > 0) {
        const hasRequiredTLD = stableRequiredTLDs.some((tld) =>
          lowerDomain.endsWith(tld.toLowerCase()),
        );
        if (!hasRequiredTLD) {
          return (
            message ||
            formatMessage(emailTLDNotAllowedMsg, {
              tlds: stableRequiredTLDs.join(', '),
            })
          );
        }
      }

      return true;
    };
  }, [
    formatMessage,
    allowDisposable,
    allowPlusAddressing,
    maxLength,
    allowInternational,
    message,
    stableAllowedDomains,
    stableBlockedDomains,
    stableRequiredTLDs,
  ]);
}
